// Primary OEM Codes — injects OEM cross-reference rows into the storefront's
// `.primary-oem-codes` table and reveals the section. Role-gated on the
// `showOemCodes` feature flag, with per-code primary/non-primary gating
// (showOemPrimary / showOemNonPrimary) matching the Partsfinder detail view.
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-oem-codes]                  — the marker/anchor element
//   data-container=".primary-oem-codes"     — optional; overrides the OEM section
//   data-sku-selector="#product-sku"        — optional; overrides the SKU source
//
// SKU source (first hit wins): the container's data-sku, then data-sku-selector /
// #product-sku, then any [data-sku] on the page.

import { getTopDocument, getTopBody, getSku as getSkuFromSelector } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';
import { loadFeatures } from '../shared/identity.js';

function findContainer() {
  return getTopDocument().querySelector('[data-parts-oem-codes]');
}

function getSku(container) {
  // 1) explicit on the container
  var direct = container && (container.getAttribute('data-sku') || '');
  if (direct && direct.trim()) return direct.trim();
  // 2) configured selector, else #product-sku, then any [data-sku] on the page
  var selector = (container && container.getAttribute('data-sku-selector')) || '#product-sku';
  return getSkuFromSelector(selector);
}

/**
 * Find (or create) the tbody inside .primary-oem-codes > table.
 * Returns { section, tbody }. The `container` (the [data-parts-oem-codes]
 * marker) is used both as the source of the data-container override and as the
 * anchor for the fallback section when the storefront section is absent.
 */
function getOemTarget(container) {
  var doc = getTopDocument();

  // Prefer an explicit container attribute first
  var section = null;
  var override = container && container.getAttribute('data-container');
  if (override) section = doc.querySelector(override);
  if (!section) section = doc.querySelector('.primary-oem-codes');

  if (!section) {
    // Storefront section not present — create a minimal fallback container
    // positioned after the marker element so the page still shows the codes.
    section = doc.createElement('div');
    section.className = 'primary-oem-codes poc-fallback';
    var label = doc.createElement('span');
    label.className = 'product-detail-oem';
    label.textContent = 'Primary OEM Codes:';
    var t = doc.createElement('table');
    t.className = 'table table-th-left poc-table';
    section.appendChild(label);
    section.appendChild(t);
    if (container && container.parentNode) {
      container.parentNode.insertBefore(section, container.nextSibling);
    } else {
      getTopBody().appendChild(section);
    }
  }

  // Find or create the table inside the section
  var table = section.querySelector('table');
  if (!table) {
    table = doc.createElement('table');
    table.className = 'table table-th-left poc-table';
    section.appendChild(table);
  } else {
    // Storefront ships this table as `table-borderless`; switch it to the
    // same `table-th-left` styling the Product Specifications table uses.
    try { table.classList.add('table', 'table-th-left', 'poc-table'); table.classList.remove('table-borderless'); } catch (e) { /* ignore */ }
  }

  // Find or create a tbody (the widget owns this tbody)
  var tbody = table.querySelector('tbody.poc-tbody');
  if (!tbody) {
    tbody = doc.createElement('tbody');
    tbody.className = 'poc-tbody';
    table.appendChild(tbody);
  }

  return { section: section, tbody: tbody };
}

function render(container, sku, features) {
  features = features || {};
  var doc = getTopDocument();
  // Use the SAME indexed part data the Partsfinder results/detail views use,
  // so the per-code `isPrimary` flag — and therefore the primary/non-primary
  // gating — is identical. The live DataGate /oem-codes lookup returned a
  // different isPrimary, which let non-primary codes through on this page.
  apiJson('/api/parts/' + encodeURIComponent(sku), null, null).then(function (data) {
    if (!data) return;
    var structured = (data && Array.isArray(data.oeNumbers)) ? data.oeNumbers : [];
    var oeNumbers;
    if (structured.length > 0) {
      // Gate primary vs non-primary independently, exactly as the
      // Partsfinder detail view does (showOemPrimary / showOemNonPrimary).
      // For Branch users showOemNonPrimary defaults off, so only primary codes show.
      oeNumbers = structured.filter(function (oe) {
        return (oe && oe.isPrimary === true)
          ? features.showOemPrimary !== false
          : features.showOemNonPrimary !== false;
      });
    } else {
      // Legacy parts without structured OE data: show flat oem_codes
      // ungated (under the showOemCodes master switch) — matches the
      // detail view's fallback for not-yet-reindexed parts.
      var flat = (data && Array.isArray(data.oem_codes)) ? data.oem_codes : [];
      oeNumbers = flat
        .map(function (c) { return { manufacturer: '', value: String(c == null ? '' : c).trim(), isPrimary: false }; })
        .filter(function (o) { return o.value; });
    }
    if (oeNumbers.length === 0) return; // nothing visible for this role — leave section hidden

    var target = getOemTarget(container);
    if (!target) return;

    var MAX_VISIBLE = 4;
    var expanded = false;

    // Sort primary OE codes to the top — DataGate's oeNumbers array can carry
    // many cross-references per part but only one is `isPrimary: true` (the OE
    // part number the vehicle's manufacturer actually fitted). Showing it first
    // makes the relationship obvious; the others read as secondary
    // cross-references underneath.
    var sortedOe = oeNumbers.slice().sort(function (a, b) {
      var ap = a && a.isPrimary === true ? 1 : 0;
      var bp = b && b.isPrimary === true ? 1 : 0;
      return bp - ap;
    });

    // Build all <tr> elements. Primary codes get an extra "PRIMARY" pill in the
    // code column so the row stands out even when the table is squashed on mobile.
    var allRows = [];
    sortedOe.forEach(function (oe) {
      var manufacturer = (oe.manufacturer || '').trim();
      var code = (oe.value || '').trim();
      if (!code) return;
      var isPrimary = oe && oe.isPrimary === true;

      var tr = doc.createElement('tr');
      if (isPrimary) tr.setAttribute('data-primary', 'true');
      // Label column as <th> so the row renders like the Product Specifications
      // table (storefront's .table-th-left styles the <th>).
      var thBrand = doc.createElement('th');
      thBrand.textContent = manufacturer || 'OE Number';
      var tdCode = doc.createElement('td');
      if (isPrimary) {
        // Use a flex row so the code + pill align cleanly regardless of the
        // storefront's default cell style.
        tdCode.style.display = 'flex';
        tdCode.style.alignItems = 'center';
        tdCode.style.gap = '8px';
        var codeSpan = doc.createElement('span');
        codeSpan.textContent = code;
        var pill = doc.createElement('span');
        pill.textContent = 'PRIMARY';
        pill.style.cssText = 'display:inline-block;font-size:9px;font-weight:700;letter-spacing:.4px;padding:2px 6px;border-radius:9999px;background:rgba(0,22,137,.1);color:#001689;text-transform:uppercase;';
        tdCode.appendChild(codeSpan);
        tdCode.appendChild(pill);
      } else {
        tdCode.textContent = code;
      }
      tr.appendChild(thBrand);
      tr.appendChild(tdCode);
      allRows.push(tr);
    });

    var hiddenCount = Math.max(0, allRows.length - MAX_VISIBLE);

    // Toggle row — spans both columns, shown only when there are hidden rows
    var toggleTr = doc.createElement('tr');
    toggleTr.className = 'poc-toggle-row';
    var toggleTd = doc.createElement('td');
    toggleTd.colSpan = 2;
    toggleTd.style.cssText = 'text-align:center;padding:8px 0 4px;border-top:1px solid #e9ecef;';
    var toggleBtn = doc.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.style.cssText = 'background:none;border:none;color:#4a6cf7;font-size:13px;font-weight:500;cursor:pointer;padding:0;display:inline-flex;align-items:center;gap:4px;';

    function updateToggle() {
      toggleBtn.innerHTML = expanded
        ? 'Show less <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="transition:transform .2s"><path d="M2 8l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : 'Show all ' + allRows.length + ' codes <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="transition:transform .2s"><path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    function applyVisibility() {
      allRows.forEach(function (tr, i) {
        tr.style.display = (expanded || i < MAX_VISIBLE) ? '' : 'none';
      });
      updateToggle();
    }

    toggleBtn.addEventListener('click', function () {
      expanded = !expanded;
      applyVisibility();
    });

    toggleTd.appendChild(toggleBtn);
    toggleTr.appendChild(toggleTd);

    // Clear any previous rows, insert all rows then the toggle
    target.tbody.innerHTML = '';
    allRows.forEach(function (tr) { target.tbody.appendChild(tr); });
    if (hiddenCount > 0) target.tbody.appendChild(toggleTr);

    applyVisibility();

    // Reveal the section (remove "hidden" class added by the storefront template)
    target.section.classList.remove('hidden');
  });
}

export function init() {
  var container = findContainer();
  if (!container) return; // not on a page with this widget

  var sku = getSku(container);
  if (!sku) {
    console.warn('PartsOemCodes: No SKU found. Add data-sku on the container, an #product-sku input, or set data-sku-selector.');
    return;
  }

  // Gate the entire widget on `showOemCodes`. Suppress only on an EXPLICIT
  // false so a features outage fails open.
  loadFeatures().then(function (features) {
    if (features && features.showOemCodes === false) {
      console.log('PartsOemCodes: feature `showOemCodes` is disabled for this role; skipping render.');
      return;
    }
    render(container, sku, features);
  });
}

export var id = 'oem-codes';
